/**
 * AdminGymRequestsPage Component
 *
 * Lists all gym requests with status filter tabs.
 * Allows admins to view and navigate to review pages for each request.
 */

import { type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminGymRequests } from '@/hooks/useAdminGymRequests';
import { GymRequestCard } from '@/components/gym-request/GymRequestCard';
import type { GymRequestStatus } from '@flash/shared';

/**
 * Filter tab type including 'all' option
 */
type FilterTab = GymRequestStatus | 'all';

/**
 * Tab configuration for filter tabs
 */
interface TabConfig {
  key: FilterTab;
  label: string;
}

const TABS: TabConfig[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'changes_requested', label: 'Changes Requested' },
];

/**
 * AdminGymRequestsPage displays all gym requests with filtering
 *
 * @example
 * ```tsx
 * // In router (wrapped in AdminRoute)
 * <Route path="/admin/gym-requests" element={<AdminRoute><AdminGymRequestsPage /></AdminRoute>} />
 * ```
 */
const VALID_TABS: FilterTab[] = ['all', 'pending', 'approved', 'rejected', 'changes_requested'];

export const AdminGymRequestsPage = () => {
  // URL-based tab state
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('status') as FilterTab | null;
  const activeTab: FilterTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'all';

  const handleTabChange = (tab: FilterTab) => {
    setSearchParams({ status: tab }, { replace: true });
  };

  const { requests, loading, error, refetch, statusCounts } = useAdminGymRequests({
    status: 'all',
  });

  // Filter requests based on active tab
  const filteredRequests =
    activeTab === 'all' ? requests : requests.filter((r) => r.status === activeTab);

  /**
   * Get count for a specific tab
   */
  const getTabCount = (tab: FilterTab): number => {
    if (tab === 'all') {
      return statusCounts.total;
    }
    return statusCounts[tab];
  };

  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading gym requests...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={errorContainerStyle}>
            <h2 style={errorTitleStyle}>Error Loading Requests</h2>
            <p style={errorTextStyle}>{error}</p>
            <button onClick={() => refetch()} style={retryButtonStyle}>
              Try Again
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <div style={headerMainStyle}>
            <div>
              <h1 style={titleStyle}>Gym Requests</h1>
              <p style={subtitleStyle}>Review and manage gym registration requests</p>
            </div>
          </div>
        </header>

        {/* Filter Tabs */}
        <div style={tabsContainerStyle}>
          <div style={tabsStyle}>
            {TABS.map((tab) => {
              const count = getTabCount(tab.key);
              const isActive = activeTab === tab.key;

              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  style={{
                    ...tabStyle,
                    ...(isActive ? activeTabStyle : {}),
                  }}
                >
                  <span>{tab.label}</span>
                  <span
                    style={{
                      ...tabCountStyle,
                      ...(isActive ? activeTabCountStyle : {}),
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Empty State */}
        {filteredRequests.length === 0 && (
          <div style={emptyStateStyle}>
            <div style={emptyIconStyle}>
              <span style={emptyIconTextStyle}>0</span>
            </div>
            <h2 style={emptyTitleStyle}>No requests found</h2>
            <p style={emptyDescriptionStyle}>
              {activeTab === 'all'
                ? 'There are no gym registration requests yet.'
                : `There are no ${activeTab.replace('_', ' ')} requests.`}
            </p>
          </div>
        )}

        {/* Requests List */}
        {filteredRequests.length > 0 && (
          <div style={listContainerStyle}>
            <div style={requestsGridStyle}>
              {filteredRequests.map((request) => (
                <GymRequestCard
                  key={request.id}
                  request={request}
                  linkTo={`/admin/gym-requests/${request.id}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        {requests.length > 0 && (
          <div style={summaryStyle}>
            <p style={summaryTextStyle}>
              Showing {filteredRequests.length} of {statusCounts.total} total requests
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
};

const contentStyle: CSSProperties = {
  maxWidth: '900px',
  margin: '0 auto',
  padding: '20px',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary)',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px 20px',
  textAlign: 'center',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '16px',
};

const retryButtonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const headerMainStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: '16px',
};

const titleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '0 0 4px 0',
};

const subtitleStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const tabsContainerStyle: CSSProperties = {
  marginBottom: '24px',
  overflowX: 'auto',
};

const tabsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  backgroundColor: 'var(--color-bg-primary)',
  padding: '8px',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
};

const tabStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 16px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  transition: 'all 0.2s',
  whiteSpace: 'nowrap',
};

const activeTabStyle: CSSProperties = {
  backgroundColor: '#3b82f6',
  color: 'white',
};

const tabCountStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '20px',
  height: '20px',
  padding: '0 6px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-secondary)',
  borderRadius: '9999px',
};

const activeTabCountStyle: CSSProperties = {
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
};

const emptyStateStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '60px 20px',
  textAlign: 'center',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
};

const emptyIconStyle: CSSProperties = {
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-bg-secondary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '20px',
};

const emptyIconTextStyle: CSSProperties = {
  fontSize: '2rem',
  color: 'var(--color-text-muted)',
  fontWeight: 600,
};

const emptyTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  margin: '0 0 8px 0',
};

const emptyDescriptionStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  maxWidth: '400px',
  lineHeight: 1.6,
  margin: 0,
};

const listContainerStyle: CSSProperties = {
  marginBottom: '24px',
};

const requestsGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '16px',
};

const summaryStyle: CSSProperties = {
  textAlign: 'center',
};

const summaryTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-muted)',
  margin: 0,
};

export default AdminGymRequestsPage;
